import {
  Card,
  CardHeader,
  CardBody,
  Typography,
} from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import {
  FilmIcon,
  MusicalNoteIcon,
  BookOpenIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { deepCopy, returnSubCategoryName } from "../helper/helper";
import { useReduxStore } from "../hooks/useReduxStore";
import { updateItem } from "../store/Reducers/itemReducer";

export default function HorizontalListCard({
  image,
  title,
  id,
  description,
  rating,
  type = "movie",
  views = 1,
  item = {},
  cast,
  sub_category = [],
  category,
  author,
  dark = false,
}) {
  const { dispatch } = useReduxStore();
  const navigate = useNavigate();

  const onClickCard = () => {
    dispatch(updateItem({ itemDetail: deepCopy(item) }));
    navigate(
      {
        pathname: "/Detail",
        search: `?id=${item?.id}`,
      },
      { state: { ...item } }
    );
  };

  const dummuImageUrl =
    "https://images.unsplash.com/photo-1499696010180-025ef6e1a8f9?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80";

  return (
    <Card
      onClick={onClickCard}
      className={`group w-full max-w-[11rem] lg:max-w-[13rem] xl:max-w-[14rem] shadow-md my-2 hover:cursor-pointer hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300 ease-out overflow-hidden rounded-xl border ${dark
          ? "bg-zinc-900 border-zinc-800 hover:bg-zinc-850 hover:border-zinc-700 text-white shadow-zinc-950/40"
          : "bg-white border-gray-100 hover:bg-orange-50/30 hover:border-orange-100 text-black shadow-gray-200/50"
        }`}
    >
      <CardHeader
        className="relative mx-0 mt-0 overflow-hidden rounded-t-xl rounded-b-none h-[10rem] lg:h-[13rem] xl:h-[14rem]"
        floated={false}
        color="transparent"
      >
        <img
          className="h-full w-full object-cover transform group-hover:scale-105 transition-transform duration-500"
          src={image || dummuImageUrl}
          alt={title || "Cover Image"}
        />
        {/* Gradient backdrop overlay for text contrast */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/30 pointer-events-none" />

        {/* Action Icon Centered on Image (reveals on hover) */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
          <div className="bg-secondary text-white p-2.5 rounded-full shadow-lg transform scale-75 group-hover:scale-100 transition-transform duration-300 ease-out">
            <EyeIcon className="h-5 w-5 stroke-[2.5]" />
          </div>
        </div>

        {/* Media Type pill overlay */}
        <div className="absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5 px-2 py-0.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-full text-white text-[10px] font-bold shadow-md">
          {type === "Video" && <FilmIcon className="h-3 w-3 text-secondary" />}
          {type === "Audio" && <MusicalNoteIcon className="h-3.5 w-3.5 text-secondary" />}
          {type === "Book" && <BookOpenIcon className="h-3.5 w-3.5 text-secondary" />}
          <span className="capitalize">{type || "Item"}</span>
        </div>

        {/* Star Rating pill overlay */}
        {rating && (
          <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-0.5 px-2 py-0.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-full text-white text-[10px] font-bold shadow-md">
            <span className="text-yellow-500 text-xs">★</span>
            <span>{Number(rating).toFixed(1)}</span>
          </div>
        )}
      </CardHeader>
      <CardBody className="px-3 pt-2.5 pb-3">
        <div className="mb-0.5 flex items-center justify-between">
          <Typography
            variant="h5"
            color={dark ? "white" : "blue-gray"}
            className="font-bold text-sm lg:text-base !line-clamp-1 group-hover:text-secondary transition-colors"
          >
            {title || "Wooden House, Florida"}
          </Typography>
        </div>
        <Typography
          variant="paragraph"
          className={`!line-clamp-1 text-xs font-normal ${dark ? "text-gray-400" : "text-gray-500"
            }`}
        >
          {author || cast || "Author"}
        </Typography>

        {/* Subcategory badge & view count */}
        <div className="mt-3.5 flex items-center justify-between">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase tracking-wider ${dark ? "bg-secondary/10 text-secondary" : "bg-orange-50 text-secondary border border-orange-100/50"
            }`}>
            {returnSubCategoryName(category) || "General"}
          </span>
          {views && (
            <span className={`text-[10px] font-medium ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
              {views} views
            </span>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
